import { compact, get, uniq } from 'lodash';
import moment from 'moment';
import { DEFAULT_ERROR, SAVE_DATE_FORMAT } from '../utils/constants';
import DatadogHandler from '../utils/datadog';
import {
  schoolService,
  centreSerivce,
  userService,
  onboardingService,
  MS_PHASE_3_ENDPOINTS,
  MS_PHASE_2B_CLASS_ACTIVITY_ENPOINT,
} from '../utils/msEndpointConstants';
import {
  transformLeadById,
  transformMCBusinessEntities,
  transformLeadsByChild,
  transformGetAllLeads,
  transformLeadActivityLogs,
} from '../utils/ms-utils/ms-phase-3/CentreWaitlistUtils';
import { executePromiseMap } from '../utils/ms-utils/ms-phase-3/CommonUtils';
import { convertObjToSnakeCase, retrieveObjectMapper } from '../utils';

const fileName = 'src/services/CentreWaitlistService.js';
const {
  CENTRE_WAITLIST,
  USER,
  SCHOOL: PHASE_3_SCHOOL,
  REGISTRATIONS,
} = MS_PHASE_3_ENDPOINTS;
const { CENTRE, SCHOOL } = MS_PHASE_2B_CLASS_ACTIVITY_ENPOINT;

export const msFetchAllTags = async () => {
  try {
    const onboardingSvc = onboardingService();
    const resp = await onboardingSvc.get(...CENTRE_WAITLIST.getAllTags());
    const data =
      resp?.data?.data?.map(tag => ({
        ...retrieveObjectMapper(tag, [
          'ID:id',
          'label',
          'description',
          'weight',
          'type:type_string',
          'code',
        ]),
      })) || [];
    return {
      data: {
        findAllTags: {
          data,
          totalCount: resp?.data?.totalCount || 0,
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchAllTags',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const getCodesByCodeId = async codeIds => {
  try {
    if (codeIds?.length === 0) {
      return {
        success: true,
        data: {
          data: [],
          totalCount: 0,
        },
      };
    }

    const schoolSvc = schoolService();
    const codeResp = await schoolSvc.post(
      ...PHASE_3_SCHOOL.getCodesByCodeId(codeIds)
    );
    const codeData = codeResp?.data?.data || [];

    return {
      success: true,
      data: {
        data: codeData,
        totalCount: codeResp?.data?.totalCount || 0,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'getCodesByCodeId',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const getLeadActivityLogByLeadChildId = async fkLeadChild => {
  try {
    const onboardingSvc = onboardingService();
    const resp = await onboardingSvc.get(
      CENTRE_WAITLIST.getLeadActivityLogByLeadChild(fkLeadChild)
    );
    const logs = resp?.data?.data || [];
    if (logs?.length === 0) {
      return {
        success: true,
        data: {
          data: [],
          totalCount: 0,
        },
      };
    }
    const fkStaffs = uniq(compact(logs?.map(log => log?.fk_staff) || []));
    const fkChannels = uniq(compact(logs?.map(log => log?.fk_channel) || []));
    const fkLeadSources = uniq(
      compact(logs?.map(log => log?.fk_lead_source) || [])
    );
    const fkMediums = uniq(compact(logs?.map(log => log?.fk_medium) || []));
    const fkCentres = uniq(compact(logs?.map(log => log?.fk_centre) || []));

    const promiseMap = {};
    if (fkStaffs?.length > 0) {
      const userSvc = userService();
      promiseMap.staffFkStaffs = userSvc.post(
        ...USER.getStaffUserByStaffIds(fkStaffs)
      );
    }

    if (fkChannels?.length > 0) {
      promiseMap.codeByFkChannels = getCodesByCodeId(fkChannels);
    }

    if (fkLeadSources?.length) {
      promiseMap.codeByFkLeadSources = getCodesByCodeId(fkLeadSources);
    }

    if (fkMediums?.length > 0) {
      promiseMap.codeByFkMediums = getCodesByCodeId(fkMediums);
    }

    if (fkCentres?.length > 0) {
      const centreSvc = centreSerivce();
      promiseMap.centreByFkCentres = centreSvc.post(
        ...CENTRE.GET_CENTRE_BY_IDS(fkCentres)
      );
    }

    const {
      staffFkStaffs,
      codeByFkChannels,
      codeByFkLeadSources,
      codeByFkMediums,
      centreByFkCentres,
    } = await executePromiseMap(promiseMap);

    const data = transformLeadActivityLogs({
      data: logs,
      staffs: staffFkStaffs?.data?.data || {},
      codeByFkChannels: codeByFkChannels?.data?.data || [],
      codeByFkLeadSources: codeByFkLeadSources?.data?.data || [],
      codeByFkMediums: codeByFkMediums?.data?.data || [],
      centreByFkCentres: centreByFkCentres?.data?.data || [],
    });
    return { data, success: true };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'getLeadActivityLogByLeadChildId',
        fileName,
      },
      'error'
    );
    return [];
  }
};

export const getLeadByChildDetails = async fkLeadChild => {
  try {
    const centreSvc = centreSerivce();
    const schoolSvc = schoolService();
    const onboardingSvc = onboardingService();

    const result = await Promise.all([
      onboardingSvc.get(
        ...CENTRE_WAITLIST.getLeadsByChildId({ ID: fkLeadChild })
      ),
      getLeadActivityLogByLeadChildId(fkLeadChild),
    ]);

    const [leadResp, activityLogs] = result;
    const leadActivityLogs = activityLogs?.data || [];
    const leadsByChild = leadResp?.data?.data || [];

    const leadIDs = compact(
      uniq(leadsByChild?.map(eachLead => eachLead?.id) || [])
    );
    const fkCentres = compact(
      uniq(leadsByChild?.map(eachLead => eachLead?.fk_centre) || [])
    );
    const fkLevels = compact(
      uniq(leadsByChild?.map(eachLead => eachLead?.fk_level) || [])
    );
    const fkPrograms = compact(
      uniq(leadsByChild?.map(eachLead => eachLead?.fk_program) || [])
    );
    const fkCancelCode = compact(
      uniq(leadsByChild?.map(eachLead => eachLead?.fk_cancel_code) || [])
    );

    const promiseMap = {};
    if (fkCentres?.length > 0) {
      promiseMap.centres = centreSvc.post(
        ...CENTRE.GET_CENTRE_BY_IDS(fkCentres)
      );
    }
    if (fkLevels?.length > 0) {
      promiseMap.levels = schoolSvc.post(...SCHOOL.GET_LEVEL_BY_IDS(fkLevels));
    }
    if (fkPrograms?.length > 0) {
      promiseMap.programs = centreSvc.post(
        ...CENTRE.GET_PROGRAMS_BY_IDS(fkPrograms)
      );
    }
    if (fkCancelCode?.length > 0) {
      promiseMap.cancelCodes = getCodesByCodeId(fkCancelCode);
    }
    if (leadIDs) {
      promiseMap.allTags = onboardingSvc.get(
        ...CENTRE_WAITLIST.getLeadTagByLeadId(leadIDs)
      );
      promiseMap.leadLastUpdatedBy = onboardingSvc.get(
        ...USER.leadLastUpdatedBy(leadIDs)
      );
      promiseMap.registrations = onboardingSvc.get(
        ...REGISTRATIONS.getRegistrationsByLeadIds(leadIDs)
      );
    }

    const promiseResp = await executePromiseMap(promiseMap);
    return {
      success: true,
      data: {
        data: [
          promiseResp?.centres,
          promiseResp?.levels,
          promiseResp?.programs,
          promiseResp?.cancelCodes,
          promiseResp?.allTags,
          promiseResp?.leadLastUpdatedBy,
          promiseResp?.registrations,
        ],
        leadActivityLogs,
        leadsByChild,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'getLeadByChildDetails',
        fileName,
      },
      'error'
    );
    return [];
  }
};

export const getMarketingConsentById = async ID => {
  try {
    const userSvc = userService();
    const onboardingSvc = onboardingService();
    const resp = await onboardingSvc.get(
      ...CENTRE_WAITLIST.getMarketingConsentById({ ID })
    );
    const marketingConsentData = resp?.data || {};
    const fkUser = marketingConsentData?.fk_user;
    const fkMarketingConsentUser =
      marketingConsentData?.fk_marketing_consent_user;

    let marketingConsentUser = null;
    if (fkMarketingConsentUser) {
      const mCUser = await onboardingSvc.get(
        USER.getMarketingConsentUserByFkMCUser(fkMarketingConsentUser)
      );
      marketingConsentUser = {
        ID: mCUser?.data?.id,
        email: mCUser?.data?.email,
      };
    }

    let user = null;
    let mcBusinessEntities = [];
    if (fkUser) {
      const [userResp, mcResp] = await Promise.all([
        userSvc.get(...USER.getUserById({ ID: fkUser })),
        onboardingSvc.get(...CENTRE_WAITLIST.getMCBusinessEntitiesById({ ID })),
      ]);
      user = retrieveObjectMapper(userResp?.data || {}, ['ID:id', 'email']);
      mcBusinessEntities = transformMCBusinessEntities(
        mcResp?.data?.data || []
      );
    }

    const marketingConsent = {
      ID: marketingConsentData?.id,
      marketingConsentUser,
      marketingConsentBusinessEntities: {
        data: mcBusinessEntities,
      },
      user,
    };

    return {
      success: true,
      data: marketingConsent,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'getMarketingConsentById',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchEnquiryDetailAction = async (_, reqData) => {
  try {
    const onboardingSvc = onboardingService();
    const result = await onboardingSvc.get(
      ...CENTRE_WAITLIST.getLeadById({ ID: reqData?.ID })
    );
    const leadData = result?.data || {};
    const fkMrketingConsent = leadData?.fk_marketing_consent;
    const leadChild = leadData?.lead_child || {};
    const fkLeadChild = leadChild?.id;

    let marketingConsentByFkMarketingConsent = {};
    if (fkMrketingConsent) {
      const resp = await getMarketingConsentById(fkMrketingConsent);
      marketingConsentByFkMarketingConsent = resp?.data || {};
    }

    let leadsByChild = {};
    let leadActivityLogs = [];
    let codeByFkParentRelationship = [];
    let codeByFkFirstChannel = [];
    let codeByFkFirstLeadSource = [];
    let codeByFkFirstMedium = [];
    let lastUpdatedBy = {};
    if (fkLeadChild) {
      const fkFirstChannel = leadChild?.fk_first_channel;
      const fkFirstLeadSource = leadChild?.fk_first_lead_source;
      const fkFirstMedium = leadChild?.fk_first_medium;
      const fkParentRelationship = leadChild?.fk_parent_relationship;

      const promiseMap = {};
      if (fkParentRelationship) {
        promiseMap.parentRelationship = getCodesByCodeId([
          fkParentRelationship,
        ]);
      }
      if (fkFirstMedium) {
        promiseMap.firstMedium = getCodesByCodeId([fkFirstMedium]);
      }
      if (fkFirstLeadSource) {
        promiseMap.firstLeadSource = getCodesByCodeId([fkFirstLeadSource]);
      }
      if (fkFirstChannel) {
        promiseMap.firstChannel = getCodesByCodeId([fkFirstChannel]);
      }
      promiseMap.leadChildLastUpdatedBy = onboardingSvc.get(
        ...USER.leadChildLastUpdatedBy([fkLeadChild])
      );

      const {
        firstMedium,
        parentRelationship,
        firstLeadSource,
        firstChannel,
        leadChildLastUpdatedBy,
      } = await executePromiseMap(promiseMap);
      codeByFkParentRelationship = parentRelationship?.data?.data?.[0] || {};
      codeByFkFirstChannel = firstChannel?.data?.data?.[0] || {};
      codeByFkFirstLeadSource = firstLeadSource?.data?.data?.[0] || {};
      codeByFkFirstMedium = firstMedium?.data?.data?.[0] || {};
      lastUpdatedBy = leadChildLastUpdatedBy?.data?.data || {};

      const response = await getLeadByChildDetails(fkLeadChild);
      const {
        data: promiseData,
        leadsByChild: leadsByChildData,
        leadActivityLogs: activityLogs,
      } = response?.data || {};
      const [
        centreResp,
        levelResp,
        programResp,
        codeResp,
        leadTagResp,
        lastUpdatedByResp,
        registrationResp,
      ] = promiseData;
      const centreData = centreResp?.data?.data || [];
      const levelData = levelResp?.data?.data || [];
      const programData = programResp?.data?.data || [];
      const codeData = codeResp?.data?.data || [];
      const allLeadTags = leadTagResp?.data?.data || {};
      const lastUpdatedByUsers = lastUpdatedByResp?.data?.data || {};
      const registrations = registrationResp?.data?.data || {};
      leadActivityLogs = activityLogs || [];

      leadsByChild = {
        data: transformLeadsByChild({
          centres: centreData,
          leads: leadsByChildData,
          levels: levelData,
          programs: programData,
          codes: codeData,
          leadTags: allLeadTags,
          lastUpdatedByUsers,
          registrations,
        }),
      };
    }

    const data = transformLeadById({
      data: leadData,
      leads: leadsByChild,
      codeByFkParentRelationship,
      codeByFkFirstChannel,
      codeByFkFirstLeadSource,
      codeByFkFirstMedium,
      leadActivityLogs,
      lastUpdatedBy,
    });

    return {
      data: {
        leadByID: {
          ...data,
          marketingConsentByFkMarketingConsent,
        },
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchEnquiryDetailAction',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msFetchCentreWaitlistAction = async (_, reqData) => {
  try {
    const centreSvc = centreSerivce();
    const schoolSvc = schoolService();
    const onboardingSvc = onboardingService();
    const params = {
      page: get(reqData, 'pagination.page', 1),
      perPage: get(reqData, 'pagination.perPage', 10),
    };
    const reqParams = {
      centre_ids: get(reqData, 'leadFilter.fkCentre', []),
      level_ids: get(reqData, 'leadFilter.fkLevel', []),
      program_ids: get(reqData, 'leadFilter.fkProgram', []),
      ...(get(reqData, 'enrollmentFrom') && {
        enrolment_from: get(reqData, 'enrollmentFrom'),
      }),
      ...(get(reqData, 'enrollmentTo') && {
        enrolment_to: get(reqData, 'enrollmentTo'),
      }),
      ...(get(reqData, 'searchKey') && {
        search_key: get(reqData, 'searchKey'),
      }),
      ...(get(reqData, 'tags') && {
        tags: get(reqData, 'tags'),
      }),
      ...(get(reqData, 'status') && {
        status: get(reqData, 'status'),
      }),
    };
    const result = await onboardingSvc.post(
      ...CENTRE_WAITLIST.getAllLeads(reqParams, params)
    );
    const findAllLeadsData = result?.data?.data || [];
    const fkCentres = compact(
      uniq(findAllLeadsData?.map(eachLead => eachLead?.fk_centre) || [])
    );
    const fkLevels = compact(
      uniq(findAllLeadsData?.map(eachLead => eachLead?.fk_level) || [])
    );
    const fkPrograms = compact(
      uniq(findAllLeadsData?.map(eachLead => eachLead?.fk_program) || [])
    );
    const promiseMap = {};
    if (fkCentres?.length > 0) {
      promiseMap.centres = centreSvc.post(
        ...CENTRE.GET_CENTRE_BY_IDS(fkCentres)
      );
    }

    if (fkLevels?.length > 0) {
      promiseMap.levels = schoolSvc.post(...SCHOOL.GET_LEVEL_BY_IDS(fkLevels));
    }

    if (fkPrograms?.length > 0) {
      promiseMap.programs = centreSvc.post(
        ...CENTRE.GET_PROGRAMS_BY_IDS(fkPrograms)
      );
    }

    const promiseData = await executePromiseMap(promiseMap);
    const { centres, levels, programs } = promiseData;
    const centreData = centres?.data?.data || [];
    const levelData = levels?.data?.data || [];
    const programData = programs?.data?.data || [];
    const transformedData = transformGetAllLeads(
      get(result, 'data.data', []),
      centreData,
      levelData,
      programData
    );
    const finalResp = {
      totalCount: get(result, 'data.totalCount', 0),
      data: transformedData,
    };
    return {
      data: {
        findAllLeads: finalResp,
      },
      success: true,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msFetchCentreWaitlistAction',
        fileName,
      },
      'error'
    );
    return {
      data: {
        findAllLeads: [],
      },
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msUpdateLeadTags = async (_, reqData) => {
  try {
    const { variables } = reqData;
    const onboardingSvc = onboardingService();
    const resp = await onboardingSvc.post(
      ...CENTRE_WAITLIST.updateLeadTags(variables?.leadID, {
        tag_ids: variables?.tagIDs,
      })
    );
    return {
      success: true,
      data: {
        updateLeadTags: resp?.data?.success || false,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUpdateLeadTags ',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msUpdateLeadChild = async (_, reqData) => {
  try {
    const { variables } = reqData;
    const reqBody = {
      street: variables?.leadChildUpdate?.address,
      block_no: variables?.leadChildUpdate?.blockNo,
      building: variables?.leadChildUpdate?.building,
      relation_with_child: variables?.leadChildUpdate?.fkParentRelationship,
      floor: variables?.leadChildUpdate?.floor,
      household_income: variables?.leadChildUpdate?.householdIncome,
      marketing_consent: variables?.leadChildUpdate?.marketingConsent || false,
      notes: variables?.leadChildUpdate?.notes,
      parent_email: variables?.leadChildUpdate?.parentEmail,
      parent_mobile: variables?.leadChildUpdate?.parentMobilePhone,
      parent_mobile_country_code:
        variables?.leadChildUpdate?.parentMobilePhoneCountryCode,
      parent_name: variables?.leadChildUpdate?.parentName,
      parent_workplace: variables?.leadChildUpdate?.parentWorkplace,
      postal_code: variables?.leadChildUpdate?.postalCode,
      unit: variables?.leadChildUpdate?.unit,
      child_race: variables?.leadChildUpdate?.race,
      child_nationality: variables?.leadChildUpdate?.nationality,
      child_gender: variables?.leadChildUpdate?.gender,
      child_name: variables?.leadChildUpdate?.childName,
      child_date_of_birth: variables?.leadChildUpdate?.childDateOfBirth,
      lead_campaign: variables?.leadChildUpdate?.campaign,
      lead_content: variables?.leadChildUpdate?.adContent,
      lead_medium: variables?.leadChildUpdate?.fkFirstMedium,
      lead_source: variables?.leadChildUpdate?.fkFirstLeadSource,
      lead_referral: variables?.leadChildUpdate?.referralCode,
    };

    const onboardingSvc = onboardingService();
    const resp = await onboardingSvc.post(
      ...CENTRE_WAITLIST.updateLeadChildDetails(variables?.leadChildId, reqBody)
    );
    return {
      success: true,
      data: resp?.data?.success || false,
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUpdateLeadChild ',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: get(error, 'message', DEFAULT_ERROR),
    };
  }
};

export const msUpdateLeadEnquiryDetails = async (_, reqData) => {
  try {
    const { variables } = reqData;
    const reqBody = {
      ...(variables?.bypassExceeding && {
        bypass_exceeding: variables?.bypassExceeding,
      }),
      ...(variables?.dateOfEnquiry && {
        date_of_enquiry: moment(variables?.dateOfEnquiry).format(
          SAVE_DATE_FORMAT
        ),
      }),
      ...(variables?.dateOfEnrolment && {
        date_of_enrolment: moment(variables?.dateOfEnrolment).format(
          SAVE_DATE_FORMAT
        ),
      }),
      ...(variables?.offerInPrinciple && {
        offer_in_principle: variables?.offerInPrinciple,
      }),
      ...(variables?.programingType && {
        program_id: variables?.programingType,
      }),
      ...(variables?.centreID && {
        centre_id: variables?.centreID,
      }),
      ...(variables?.levelId && {
        level_id: variables?.levelId,
      }),
    };
    const onboardingSvc = onboardingService();
    const resp = await onboardingSvc.post(
      ...CENTRE_WAITLIST.updateLeadEnquiryDetails(variables?.leadID, reqBody)
    );
    const data =
      resp?.data &&
      retrieveObjectMapper(resp?.data, ['ID:id', 'enrolment_date', 'remarks']);
    return {
      success: true,
      data: {
        UpdateLeadEnquiryDetail: data,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msUpdateLeadEnquiryDetails ',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: [
        {
          message:
            get(error, 'response.data.error') ||
            get(error, 'message') ||
            DEFAULT_ERROR,
          errCode: get(error, 'response.data.errCode') || get(error, 'code'),
        },
      ],
    };
  }
};

export const msOfferLead = async (_, reqData) => {
  try {
    const onboardingSvc = onboardingService();
    const resp = await onboardingSvc.post(
      ...CENTRE_WAITLIST.offerLead(reqData)
    );
    return {
      success: true,
      data: {
        offerLead: retrieveObjectMapper(get(resp, 'data', {}), [
          'ID:id',
          'fk_lead_child',
          'fk_centre',
          'fk_program',
        ]),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msOfferLead',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      errors: [
        {
          message: get(error, 'response.data.error', DEFAULT_ERROR),
          errCode: get(error, 'response.data.errCode'),
        },
      ],
    };
  }
};

export const msApproveLead = async (_, reqData) => {
  try {
    const body = {
      ...(get(reqData, 'bypassExceeding') && {
        bypass_exceeding: get(reqData, 'bypassExceeding'),
      }),
      registration_data: {
        ...retrieveObjectMapper(get(reqData, 'registrationDto', {}), [
          'fkLead',
          'centre_id:centreId',
          'programme_id:programmeId',
          'child_birth_certificate:childBirthCertificate',
          'child_date_of_birth:childDateOfBirth',
          'child_name:childName',
          'child_nationality:childNationality',
          'email',
          'enquiry_date:enquiryDate',
          'enrolment_date:enrolmentDate',
          'level_id:levelId',
          'name',
          'phone',
          'phone_country_code:phoneCountryCode',
          'relationship_id:relationshipID',
          'work_place_staff:workPlaceStaff',
        ]),
      },
    };
    const resp = await onboardingService().post(
      ...CENTRE_WAITLIST.approveLead(
        get(reqData, 'registrationDto.fkLead'),
        body
      )
    );
    return {
      success: true,
      data: {
        assignLead: retrieveObjectMapper(get(resp, 'data', {}), ['ID:id']),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msApproveLead',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      errors: [
        {
          message: get(error, 'response.data.error', DEFAULT_ERROR),
          errCode: get(error, 'response.data.errCode'),
        },
      ],
    };
  }
};

export const msCancelLead = async (_, reqData) => {
  try {
    const resp = await onboardingService().post(
      ...CENTRE_WAITLIST.cancelLead(get(reqData, 'leadID'), reqData)
    );
    return {
      success: true,
      data: {
        cancelLead: retrieveObjectMapper(get(resp, 'data', {}), ['ID:id']),
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msCancelLead',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      errors: [
        {
          message: get(error, 'response.data.error', DEFAULT_ERROR),
          errCode: get(error, 'response.data.errCode'),
        },
      ],
    };
  }
};

export const msAddInteraction = async (_, reqData) => {
  try {
    const reqDto = reqData?.variables?.dto;
    const centreIds = reqData?.variables?.fkCentreIds || [];
    if (!reqDto || centreIds?.length === 0) {
      return {
        success: false,
        data: {
          addInteraction: false,
        },
      };
    }

    const reqBody = {
      centreIDs: centreIds,
      leadInteractionDto: {
        ...(reqDto?.adContent && {
          ad_content: reqDto?.adContent,
        }),
        ...(reqDto?.campaign && {
          campaign: reqDto?.campaign,
        }),
        ...(reqDto?.fkCentre && {
          centre_id: reqDto?.fkCentre,
        }),
        ...(reqDto?.fkChannel && {
          channel_id: reqDto?.fkChannel,
        }),
        ...(reqDto?.correspondence && {
          correspondence: reqDto?.correspondence,
        }),
        ...(reqDto?.enquireDate && {
          enquire_date: reqDto?.enquireDate,
        }),
        ...(reqDto?.fkLeadId && {
          lead_id: reqDto?.fkLeadId,
        }),
        ...(reqDto?.fkLeadSource && {
          lead_source_id: reqDto?.fkLeadSource,
        }),
        ...(reqDto?.fkMedium && {
          medium_id: reqDto?.fkMedium,
        }),
        ...(reqDto?.otherLeadSource && {
          other_lead_source: reqDto?.otherLeadSource,
        }),
        ...(reqDto?.parentEmail && {
          parent_email: reqDto?.parentEmail,
        }),
        ...(reqDto?.parentMobilePhone && {
          parent_mobile_phone: reqDto?.parentMobilePhone,
        }),
        ...(reqDto?.parentMobilePhoneCountryCode && {
          parent_mobile_phone_country_code:
            reqDto?.parentMobilePhoneCountryCode,
        }),
        ...(reqDto?.parentName && {
          parent_name: reqDto?.parentName,
        }),
        ...(reqDto?.referralCode && {
          referral_code: reqDto?.referralCode,
        }),
        ...(reqDto?.fkStaff && {
          staff_id: reqDto?.fkStaff,
        }),
        ...(reqDto?.subject && {
          subject: reqDto?.subject,
        }),
        ...(reqDto?.timeOfCorrespondence && {
          time_of_correspondence: reqDto?.timeOfCorrespondence,
        }),
      },
    };
    const onboardingSvc = onboardingService();
    const resp = await onboardingSvc.post(
      ...CENTRE_WAITLIST.addInteraction(reqDto?.fkLeadChild, reqBody)
    );
    return {
      success: true,
      data: {
        addInteraction: resp?.data?.success || false,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msAddInteraction ',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      errors: [
        {
          message:
            get(error, 'response.data.error') ||
            get(error, 'message') ||
            DEFAULT_ERROR,
          errCode: get(error, 'response.data.errCode') || get(error, 'code'),
        },
      ],
    };
  }
};

export const msGetModifiedLeadVacancy = async (_, reqData) => {
  try {
    const body = convertObjToSnakeCase(reqData, [
      'centre_id:centreID',
      'lead_id:leadID',
      'level_id:levelID',
      'program_id:programID',
    ]);
    const resp = await onboardingService().post(
      ...CENTRE_WAITLIST.getModifiedLeadVacancy(body)
    );

    return {
      success: true,
      data: {
        getModifiedLeadVacancy: resp?.data?.success || false,
      },
    };
  } catch (error) {
    DatadogHandler.addError(error);
    DatadogHandler.sendLog(
      error,
      {
        functionName: 'msGetModifiedLeadVacancy',
        fileName,
      },
      'error'
    );
    return {
      success: false,
      error: {
        message:
          get(error, 'response.data.error') ||
          get(error, 'message') ||
          DEFAULT_ERROR,
      },
    };
  }
};

export default msFetchAllTags;
